import type { WaitSoundVolume } from "@/types/ai-assistant-style";
import {
	BREATH_FILTER_Q,
	BREATH_LOWPASS_HZ,
	BREATH_MS,
	breathCurve,
	breathEnvelope,
	breathFilterHz,
	WAIT_SOUND_GAIN,
} from "./voice-breath";

/**
 * Pemutar tipis bunyi napas lewat Web Audio: derau putih berulang → bandpass →
 * lowpass → gain berkurva → gain induk (untuk fade-out saat dihentikan). Tanpa
 * file audio. Konteks audio dibuat/di-resume lewat `prime()` saat user menekan On.
 */

/** Panjang buffer derau (detik); diputar berulang. */
const NOISE_SECONDS = 2;
/** Fade-out saat dihentikan sebelum napas selesai (detik). */
const STOP_FADE_S = 0.03;
/** Sumber derau dimatikan sedikit setelah fade supaya tidak terpotong. */
const STOP_AFTER_S = 0.2;

export interface BreathPlayer {
	/** Buat/lanjutkan AudioContext — panggil dari klik On supaya browser mengizinkan. */
	prime(): void;
	/** Mulai satu napas; `onEnded` dipanggil saat selesai sendiri (bukan saat `stop`). */
	play(volume: WaitSoundVolume, onEnded: () => void): void;
	/** Hentikan napas yang berjalan dengan fade-out singkat. */
	stop(): void;
	/** Tutup AudioContext. */
	close(): void;
}

interface Playing {
	source: AudioBufferSourceNode;
	master: GainNode;
	timer: ReturnType<typeof setTimeout>;
}

function noiseBuffer(ctx: AudioContext): AudioBuffer {
	const length = Math.floor(ctx.sampleRate * NOISE_SECONDS);
	const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
	const data = buffer.getChannelData(0);
	for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
	return buffer;
}

/** Browser tanpa Web Audio (atau lingkungan test): tidak ada bunyi, suara tetap jalan. */
const defaultContext = (): AudioContext | null =>
	typeof AudioContext === "undefined" ? null : new AudioContext();

export function createBreathPlayer(
	createContext: () => AudioContext | null = defaultContext,
): BreathPlayer {
	let ctx: AudioContext | null = null;
	let noise: AudioBuffer | null = null;
	let playing: Playing | null = null;

	const ensure = (): AudioContext | null => {
		if (!ctx || ctx.state === "closed") {
			ctx = createContext();
			noise = null;
		}
		return ctx;
	};

	const stopPlaying = () => {
		if (!playing || !ctx) return;
		const { source, master, timer } = playing;
		playing = null;
		clearTimeout(timer);
		const now = ctx.currentTime;
		master.gain.cancelScheduledValues(now);
		master.gain.setTargetAtTime(0, now, STOP_FADE_S);
		source.stop(now + STOP_AFTER_S);
	};

	return {
		prime() {
			const c = ensure();
			if (c?.state === "suspended")
				c.resume().catch(() => {
					// Resume ditolak browser: buang konteks agar play() berikutnya membuat yang baru.
					ctx = null;
				});
		},
		play(volume, onEnded) {
			const c = ensure();
			if (!c) return;
			stopPlaying();
			noise ??= noiseBuffer(c);
			const source = c.createBufferSource();
			source.buffer = noise;
			source.loop = true;
			const band = c.createBiquadFilter();
			band.type = "bandpass";
			band.Q.value = BREATH_FILTER_Q;
			const low = c.createBiquadFilter();
			low.type = "lowpass";
			low.frequency.value = BREATH_LOWPASS_HZ;
			const shape = c.createGain();
			shape.gain.value = 0;
			const master = c.createGain();
			master.gain.value = 1;
			source.connect(band);
			band.connect(low);
			low.connect(shape);
			shape.connect(master);
			master.connect(c.destination);
			const t0 = c.currentTime;
			const seconds = BREATH_MS / 1000;
			band.frequency.setValueCurveAtTime(
				breathCurve(breathFilterHz),
				t0,
				seconds,
			);
			shape.gain.setValueCurveAtTime(
				breathCurve(breathEnvelope, WAIT_SOUND_GAIN[volume]),
				t0,
				seconds,
			);
			source.start(t0);
			source.stop(t0 + seconds + STOP_AFTER_S);
			const timer = setTimeout(() => {
				playing = null;
				onEnded();
			}, BREATH_MS);
			playing = { source, master, timer };
		},
		stop: stopPlaying,
		close() {
			stopPlaying();
			const c = ctx;
			ctx = null;
			if (c && c.state !== "closed") void c.close();
		},
	};
}
